/* eslint-disable @typescript-eslint/naming-convention */
import mixpanel from 'mixpanel-browser';
import { MIXPANEL_TOKEN, ENVIRONMENT } from '@/constants/Config';
import {
  resolveMixpanelInboundSource,
  resolveMixpanelReferrerProperties,
} from '@/mixpanel/inboundEntryTracking';

mixpanel.init(MIXPANEL_TOKEN, {
  persistence: 'localStorage',
  record_sessions_percent: ENVIRONMENT === 'production' ? 20 : 1,
  record_block_selector: '',
  record_mask_text_selector: '',
});

mixpanel.register({ 'Site Type': 'HQ' });

declare module 'mixpanel-browser' {
  interface OverridedMixpanel {
    secondary?: OverridedMixpanel;
  }
}

const MixpanelUtils = {
  setup: () => {
    mixpanel.register({ Platform: 'web' });
    mixpanel.secondary?.register({ Platform: 'web' });
  },

  createAlias: (alias: string) => {
    mixpanel.alias(alias, mixpanel.get_distinct_id());
    mixpanel.secondary?.alias(alias, mixpanel.get_distinct_id());
  },

  identify: (userEmail: string) => {
    mixpanel.identify(userEmail);
    mixpanel.secondary?.identify(userEmail);
  },

  register: (properties: object = {}) => {
    mixpanel.register(properties);
    mixpanel.secondary?.register(properties);
  },

  track: (name: string, action: object = {}) => {
    mixpanel.track(name, action);
    mixpanel.secondary?.track(name, action);
  },

  setPeople: (properties: object = {}) => {
    mixpanel.people.set(properties);
    mixpanel.secondary?.people.set(properties);
  },

  appendPeople: (properties: object = {}) => {
    mixpanel.people.append(properties);
    mixpanel.secondary?.people.append(properties);
  },

  incrementPeopleProperty: (property: string) => {
    mixpanel.people.increment(property);
    mixpanel.secondary?.people.increment(property);
  },

  setSuperProperties: (properties: object) => {
    mixpanel.register(properties);
    mixpanel.secondary?.register(properties);
  },

  sendEvent: (event: string, properties: object) => {
    const referrerProps = resolveMixpanelReferrerProperties();
    const inboundSource = resolveMixpanelInboundSource();
    const propsRecord = properties as Record<string, unknown>;
    const enriched = {
      ...referrerProps,
      ...properties,
      ...(propsRecord.Source === undefined &&
        propsRecord.source === undefined &&
        inboundSource && { Source: inboundSource }),
    };

    const filtered = Object.fromEntries(
      Object.entries(enriched).filter(([, v]) => v !== null && v !== undefined)
    );
    mixpanel.track(event, filtered);
    mixpanel.secondary?.track(event, filtered);
  },

  logout: () => {
    mixpanel.reset();
    mixpanel.secondary?.reset();
  },

  appendMultipleProperties: (properties: object = {}) => {
    mixpanel.people.union(properties);
    mixpanel.secondary?.people.union(properties);
  },

  setPeopleOnce: (properties: object) => {
    mixpanel.people.set_once(properties);
    mixpanel.secondary?.people.union(properties);
  },
};

export default MixpanelUtils;
